import { useEffect, useState } from 'react'

/**
 * Ujęcie z Projektu 3D na okładkę dokumentu „Oferta i Projekt” (decyzja właściciela 2026-10-11: render
 * w przeglądarce przy otwarciu dokumentu, bez zapisu obrazka na serwerze).
 *
 * Model trafia do `localStorage` pod `vv_ujecie_3d` (ten sam klucz w pages/ProjektDachu3D.jsx), a strona
 * `/projekt-3d?ujecie=1` ładuje się w niewidocznej ramce 1600 × 1000 px: wczytuje dane GUGiK, ustawia kadr
 * i światło, robi zdjęcia i odsyła je wiadomością. Ramka jest w oknie (przezroczysta, pod spodem), a nie poza
 * ekranem: przeglądarki wstrzymują rysowanie w ramkach poza widokiem. Wynik trzymamy w pamięci karty,
 * więc ponowne otwarcie dokumentu dla tego samego modelu nie liczy wszystkiego od nowa.
 *
 * Zwraca: null (brak modelu), { trwa: true }, { blad: true } albo { glowne, zGory, pola, kwp, rokKwh, strataCienia }
 * z obrazami JPEG jako data URI.
 */

const KLUCZ = 'vv_ujecie_3d'
const LIMIT_MS = 90000
const pamiec = new Map()

const skrot = (tekst) => {
  let h = 0
  for (let i = 0; i < tekst.length; i += 1) h = Math.imul(31, h) + tekst.charCodeAt(i) | 0
  return `${tekst.length}:${h}`
}

/** PNG z WebGL bywa ciężki (kilka MB); JPEG 0,9 to ułamek tego przy tej samej jakości na wydruku. */
const naJpeg = (dataUri) => new Promise((zwroc) => {
  if (!dataUri) { zwroc(null); return }
  const obraz = new Image()
  obraz.onload = () => {
    const c = document.createElement('canvas')
    c.width = obraz.width
    c.height = obraz.height
    const ctx = c.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, c.width, c.height)
    ctx.drawImage(obraz, 0, 0)
    zwroc(c.toDataURL('image/jpeg', 0.9))
  }
  obraz.onerror = () => zwroc(null)
  obraz.src = dataUri
})

const useUjecie3D = (projekt) => {
  const klucz = projekt?.lokalizacja ? skrot(JSON.stringify(projekt)) : null
  const [stan, setStan] = useState(() => (klucz ? pamiec.get(klucz) || null : null))

  useEffect(() => {
    if (!klucz) { setStan(null); return undefined }
    const gotowe = pamiec.get(klucz)
    if (gotowe) { setStan(gotowe); return undefined }
    setStan({ trwa: true })
    try { window.localStorage.setItem(KLUCZ, JSON.stringify(projekt)) } catch { setStan({ blad: true }); return undefined }

    const ramka = document.createElement('iframe')
    ramka.src = `${process.env.PUBLIC_URL || ''}/projekt-3d?ujecie=1`
    ramka.setAttribute('aria-hidden', 'true')
    ramka.tabIndex = -1
    Object.assign(ramka.style, {
      position: 'fixed', left: '0', top: '0', width: '1600px', height: '1000px', border: '0',
      opacity: '0', pointerEvents: 'none', zIndex: '-1',
    })
    let skonczone = false
    const zakoncz = (wynik) => {
      if (skonczone) return
      skonczone = true
      window.removeEventListener('message', odbierz)
      clearTimeout(limit)
      ramka.remove()
      if (!wynik.blad) pamiec.set(klucz, wynik)
      setStan(wynik)
    }
    const odbierz = async (e) => {
      if (e.origin !== window.location.origin || e.source !== ramka.contentWindow || e.data?.typ !== 'vv-ujecie-3d') return
      const [glowne, zGory] = await Promise.all([naJpeg(e.data.glowne), naJpeg(e.data.zGory)])
      zakoncz(glowne ? { ...e.data, glowne, zGory } : { blad: true })
    }
    const limit = setTimeout(() => zakoncz({ blad: true }), LIMIT_MS)
    window.addEventListener('message', odbierz)
    document.body.appendChild(ramka)
    return () => {
      if (!skonczone) {
        skonczone = true
        window.removeEventListener('message', odbierz)
        clearTimeout(limit)
        ramka.remove()
      }
    }
    // `projekt` jest w kluczu (skrót z JSON), więc zależność od klucza wystarcza.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klucz])

  return stan
}

export default useUjecie3D
